/**
 * CetusEd Admin Console — Admin Authentication Provider
 *
 * COMPLIANCE: Admin access requires MFA verification per NIST SP 800-63B AAL3.
 *   Vendor support staff must have elevated RBAC capabilities with explicit
 *   grants for cross-district operations. All auth events are audit-logged.
 *
 *   FERPA §1232g(b)(4)(B) — Vendor access to PII is limited to authorized
 *   personnel with a valid DPA in place. The admin JWT includes the actor's
 *   role and scope limitations.
 */

import React, { createContext, useCallback, useMemo, useReducer } from 'react';
import { AppError } from '@cetused/shared';
import { setAdminToken } from '../api/client';

export const ADMIN_TOKEN_KEY = '_ADMIN_AUTH_TOKEN';

interface AdminAuthState {
  token: string | null;
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'support' | 'district_admin' | 'read_only';
  mfaVerified: boolean;
  capabilities: string[];
}

type AdminAuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; token: string; user: AdminUser }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'RESTORE_SESSION'; token: string; user: AdminUser };

function adminAuthReducer(state: AdminAuthState, action: AdminAuthAction): AdminAuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true };
    case 'LOGIN_SUCCESS':
      return { token: action.token, user: action.user, isAuthenticated: true, isLoading: false };
    case 'LOGIN_FAILURE':
      return { token: null, user: null, isAuthenticated: false, isLoading: false };
    case 'LOGOUT':
      return { token: null, user: null, isAuthenticated: false, isLoading: false };
    case 'RESTORE_SESSION':
      return { token: action.token, user: action.user, isAuthenticated: true, isLoading: false };
    default:
      return state;
  }
}

function loadPersistedAdminAuth(): AdminAuthState {
  try {
    const raw = sessionStorage.getItem(ADMIN_TOKEN_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.token && parsed.user && parsed.user.mfaVerified) {
        setAdminToken(parsed.token);
        return { token: parsed.token, user: parsed.user, isAuthenticated: true, isLoading: false };
      }
    }
  } catch {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  }
  return { token: null, user: null, isAuthenticated: false, isLoading: false };
}

interface AdminAuthContextValue extends AdminAuthState {
  login: (email: string, password: string, mfaCode: string) => Promise<void>;
  logout: () => void;
}

export const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(adminAuthReducer, undefined, loadPersistedAdminAuth);

  const login = useCallback(async (email: string, password: string, mfaCode: string) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await fetch('/api/admin/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, mfaCode }),
      });
      const json = await response.json();
      if (!response.ok || !json.success) {
        throw AppError.unauthorized(json.error || 'Authentication failed');
      }
      const { token, user } = json.data;
      if (!user.mfaVerified) {
        throw AppError.unauthorized('MFA verification required');
      }
      setAdminToken(token);
      sessionStorage.setItem(ADMIN_TOKEN_KEY, JSON.stringify({ token, user }));
      dispatch({ type: 'LOGIN_SUCCESS', token, user });
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    setAdminToken(null);
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    dispatch({ type: 'LOGOUT' });
  }, []);

  const value = useMemo(() => ({ ...state, login, logout }), [state, login, logout]);

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};
